/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import _ from 'underscore';
import StringResources from 'nls/StringResources';
import Config from './Config';
import DataBuilder from './DataBuilder';
const GROUP_TYPES_PREFIXES = {
	HIERARCHY: 'hierarchy_',
	CONCEPT: 'concept_',
	NONE: 'none_'
};

const MESSAGE_STATUS = {
	WARN: 'warn'
};

const REQUEST_CATEGORY = {
	LINKDATASET: 'LinkDataset',
	NODEDATASET: 'NodeDataset'
};

function getFieldLabel(uid, metadataInfo){
	if(metadataInfo){
		const field = metadataInfo.find(field => field.id === uid);
		if (field) {
			return field.name;
		}
	}
	return uid;
}

function getGroupLabel(groupUid, metadataInfo){
	const groupUidLength = groupUid.length;
	if (groupUid.startsWith(GROUP_TYPES_PREFIXES.HIERARCHY)) {
		return groupUid.substr(GROUP_TYPES_PREFIXES.HIERARCHY.length, groupUidLength);
	}
	if (groupUid.startsWith(GROUP_TYPES_PREFIXES.CONCEPT)) {
		return groupUid.substr(GROUP_TYPES_PREFIXES.CONCEPT.length, groupUidLength);
	}
	if (groupUid.startsWith(GROUP_TYPES_PREFIXES.NONE)) {
		return getFieldLabel(groupUid.substr(GROUP_TYPES_PREFIXES.NONE.length, groupUidLength), metadataInfo);
	}
	return '';
}

function getGroupTypeAndGroupLabel(groupUid){
	const groupUidLength = groupUid.length;
	const result = {
		groupType: null,
		groupLabel: null
	};
	if (groupUid.startsWith(GROUP_TYPES_PREFIXES.NONE)) {
		result.groupLabel = groupUid.substr(GROUP_TYPES_PREFIXES.NONE.length, groupUidLength);
		result.groupType = 'none';
		return result;
	}
	if (groupUid.startsWith(GROUP_TYPES_PREFIXES.HIERARCHY)) {
		result.groupLabel = groupUid.substr(GROUP_TYPES_PREFIXES.HIERARCHY.length, groupUidLength);
		result.groupType = 'hierarchy';
		return result;
	}
	if (groupUid.startsWith(GROUP_TYPES_PREFIXES.CONCEPT)) {
		result.groupLabel = groupUid.substr(GROUP_TYPES_PREFIXES.CONCEPT.length, groupUidLength);
		result.groupType = 'concept';
		return result;
	}

	return result;
}

/**
* @param {JSONObject} rawData
* @param {JSONObject} options
* @return {JSONObject} return an object that holds the status of verifycation status and string of details
*/
function getDataVerificationResults(rawData, options){
	return {
		'linkDataset' : _verifyLinkDataset(rawData, options),
		'nodeDataset' : _verifyNodeDataset()
	};
}

function _verifyLinkDataset(rawData, options){
	if(rawData && rawData.relationshipStrength && rawData.relationshipStrength.predictStatus){
		if('ERROR' === rawData.relationshipStrength.predictStatus.state){
			//By design we output warnings for link dataset errors
			const targetId = options ? options.targetId : 'unknown';
			return _buildVerificationResult(MESSAGE_STATUS.WARN, targetId, REQUEST_CATEGORY.LINKDATASET);
		}
	}
	return null;
}

//TODO: Augment this when Service end's informaiton is available
function _verifyNodeDataset(/** rawData, options */){
	return null;
}

function _buildVerificationResult(status, targetId, category){
	//Console Debugging Message only, localization is not needed
	const err_details_linkDataset = `An error occurs when querying relationship strengths (link datasets) for target: ${targetId}`;
	const err_details_nodeDataset = `An error occurs when querying relationship nodes (node datasets) for target: ${targetId}`;
	const details = category === REQUEST_CATEGORY.LINKDATASET ? err_details_linkDataset : err_details_nodeDataset;
	return {
		status: status,
		category: category,
		details: details
	};
}

//Console Error for debugging
function printMsg(msgObject){
	if(msgObject && msgObject.status === MESSAGE_STATUS.WARN){
		console.warn(msgObject.details);
	}
}

/**
 * Transform raw data before preparing it for VIDA
 * @param {object} context - contains all information needed for transformations, including:
 * rawData {object} - Predict response for request of 100 nodes
 * diagramData {object} - diagram data saved last time in RelationshipTargetModel
 * transformOptions: {object} - transform operation and its parameters
 *   e.g. `deletedFields` with list of fields to remove
 *
 * @return {object} - result in same format as data, mutated by transforms
 */
function applyTransforms(context) {
	const transformKey = Object.keys(context.transformOptions)[0];
	let data = null;
	const targetModel = context.targetModel;
	switch(transformKey) {
		case 'filterStrength':
		case 'showPrimaryRelationshipView':
		case 'scopedFields': //we have new scope, rerender the relationship diagram
			data = filterRelationships(context.rawData, context);
			break;
		case 'deletedFields':
			data = removeDeletedFields(context.diagramData, context.transformOptions.deletedFields);
			data = removeOrphanFields(data);
			break;
		case 'lastAddedFields': {
			const result = addFields(context.diagramData, context.rawData, { addedFields: context.transformOptions.lastAddedFields, targetId: context.model.getCurrentTargetId(), showPrimaryRelationshipView: context.targetModel.getDiagramFilterCondition().showPrimaryRelationshipView });
			setInfoMessageForAddedFields(result, context);
			data = context.diagramData;
		}
			break;
		case 'lastShowRelationshipsField': {
			const { from, to } = context.transformOptions.lastShowRelationshipsField;
			let { relationship } = context.transformOptions.lastShowRelationshipsField;
			const { diagramData } = context;
			relationship = _normalizeRelationship(relationship);
			const result = showRelationship(from, to, relationship, diagramData, { targetModel, showMessage: true });
			if(result && result.isToNodeNotInDiagram === true) {
				// Add `to` into `addedFields`
				targetModel.addToAddedFields([to]);
			}
			targetModel.setRelationship(from, to, relationship);
			data = diagramData;
		}
			break;
		default:
			// this is for unit test
			data = context.diagramData || context.rawData;
	}
	if (data && targetModel) {
		targetModel.setDiagramData(data);
	}
	return data;
}

function setInfoMessageForAddedFields(result, context) {
	if(result && result.unfoundNodeIds.length) {
		const showPrimaryRelationshipView = context.targetModel.getDiagramFilterCondition().showPrimaryRelationshipView;
		let message = '';
		if (context.transformOptions.lastAddedFields.length === 1) {
			message = StringResources.get('startingPoints_no_relationship_found');
		} else {
			if (showPrimaryRelationshipView) {
				message = StringResources.get('startingPoints_no_direct_relationship_withNumberNotFound',  { notFoundNodesNumber: JSON.stringify(result.unfoundNodeIds.length), totalAddedNodesNumber: JSON.stringify(context.transformOptions.lastAddedFields.length) });
			} else {
				message = StringResources.get('startingPoints_no_relationship_withNumberNotFound',  { notFoundNodesNumber: JSON.stringify(result.unfoundNodeIds.length), totalAddedNodesNumber: JSON.stringify(context.transformOptions.lastAddedFields.length) });
			}
		}
		context.targetModel.setActionInfoStatus(message);
	}
}

/**
 * Given a field in data, identifies all other fields related to it directly and
 * indirectly.
 * @param {object} data
 * @param {string} root
 * @return {string[]}
 */
function getConnectedFields(data, root) {

	//Get copy of all relationships for fast look-up
	const fieldRelationships = {};
	const addRelationship = (source, target) => {
		if(!fieldRelationships[source]) {
			fieldRelationships[source] = [];
		}
		fieldRelationships[source].push(target);
	};
	data.relationshipStrength.results.forEach(
		({ relationships }) => relationships.forEach(
			({ fieldA, fieldB }) => {
				addRelationship(fieldA, fieldB);
				addRelationship(fieldB, fieldA);
			}
		)
	);

	const visitedFields = {};
	let pendingFields = [root];

	while(pendingFields.length) {
		const field = pendingFields.pop();
		if(!visitedFields[field]) {
			visitedFields[field] = true;
			const related = fieldRelationships[field] || [];
			pendingFields = pendingFields.concat(related);
		}
	}

	return _.keys(visitedFields);
}

/**
 * Identifies any fields orphaned from the target field.
 * @param {object} data
 * @return {string[]}
 */
function getOrphanFields(data) {
	if (!(data && data.nodesDataset)) {
		return data; // this is for unit test to mock this function
	}
	const allFields = _.flatten(
		data.nodesDataset.groupings.map(
			grouping => grouping.groups.map(
				group => group.members.map(({ id }) => id)
			)
		)
	);

	const connectedFields = getConnectedFields(data, data.nodesDataset.targetId);

	return _.difference(allFields, connectedFields);
}

/**
 * Modifies `data` to only include fields and links related to the target field
 * @param {object} data
 * @return {object}
 */
function removeOrphanFields(data) {
	return removeDeletedFields(data, getOrphanFields(data));
}

/**
 * Modifies `data` to exclude fields and links involving `deletedFields`
 * @param {object} data
 * @param {string[]} deletedFields
 * @return {object}
 */
function removeDeletedFields(data, deletedFields) {
	const isDeletedField = field => deletedFields.indexOf(field) > -1;
	if (!(data && data.nodesDataset)) {
		return data; // this is for unit test to mock this function
	}
	return {
		...data,
		nodesDataset: {
			...data.nodesDataset,
			groupings: data.nodesDataset.groupings.map(
				grouping => ({
					...grouping,
					groups: grouping.groups.map(
						group => ({
							...group,
							//Only keep members if they haven't been deleted
							members: group.members.filter(
								({ id }) => !isDeletedField(id)
							)
						})
					).filter(
						//Only keep groups if they have members which have not been filtered out
						({ members }) => !!members.length
					)
				})
			)
		},
		relationshipStrength: {
			...data.relationshipStrength,
			results: data.relationshipStrength.results.map(
				result => ({
					...result,
					relationships: result.relationships.filter(
						({ fieldA, fieldB }) => !isDeletedField(fieldA) && !isDeletedField(fieldB)
					)
				})
			)
		}
	};
}
/**
* This function filters raw data from Predict response to return only nodes and links satisfying filter strength and view type (primary/secondary relationship view) then apply delete/add fields if necessary
* @param {object} rawData - raw data from Predict response
* @param {object} context - contains target model reference
* @return {object} filtered data and calculated filter strength (if first time loading)
*/
function filterRelationships(rawData, context) {
	const targetModel = context.targetModel;
	const scopedRawData = getScopedRawData(rawData, targetModel) || rawData;
	const filterCondition = targetModel.getDiagramFilterCondition();
	let result = scopedRawData;
	// filterCondition null only used in unit test, should not happen in production
	if (filterCondition) {
		result = filterRelationshipStrength(scopedRawData, filterCondition);
		if (result && (result.distinctNodes.length)) {
			// update slider info
			targetModel.updateInitialFilterStrength(result.filterStrengthInfo);
			result = formatFilterResult(scopedRawData, result);
			const deletedFields = targetModel.getDeletedFields();
			if (deletedFields.length) {
				result = removeDeletedFields(result, deletedFields);
				result = removeOrphanFields(result);
			}
			const addedFields = targetModel.getAddedFields();
			if (addedFields.length) {
				const targetId = filterCondition.targetId;
				const showPrimaryRelationshipView = filterCondition.showPrimaryRelationshipView;
				addFields(result, rawData, { addedFields, targetId, showPrimaryRelationshipView });
			}

			const showRelationshipsMap = targetModel.getShowRelationships();
			const froms = Object.keys(showRelationshipsMap);
			if (froms.length) {
				froms.forEach((from) => {
					const toEntries = showRelationshipsMap[from];
					toEntries.forEach((toEntry) => {
						showRelationship(from, toEntry.to, toEntry.relationship, result);
					});
				});
			}
		}
	}
	return result;
}

function _normalizeRelationship(relationship) {
	if(relationship) {
		//do a deep copy
		relationship = JSON.parse(JSON.stringify(relationship));
		const { toNode } = relationship;
		//remove the type, for the case the `toNode` is the target in the cache
		if(toNode && toNode.members && toNode.members.length > 0){
			delete toNode.members[0].type;
		}
	}
	return relationship;
}

function showRelationship(from, to, relationship, diagramData, options = {}) {
	const fromNodeInDiagramData = findSingleNodeFromCache(from, diagramData);
	//if `from` field is not in current diagram, return
	if(!fromNodeInDiagramData) {
		return;
	}
	const result = {};
	let message = null;
	if(relationship) {
		const { toNode, link } = relationship;
		if(!findSingleNodeFromCache(to, diagramData)) {
			//if the `to` field is not in current diagram, add it
			const currentgroups = diagramData.nodesDataset.groupings[0].groups;
			currentgroups.push(toNode);
			result.isToNodeNotInDiagram = true;
		}
		const currentRelationships = diagramData.relationshipStrength.results[0].relationships;
		if(!currentRelationships.find((currentRelationship) => _.isEqual(currentRelationship, link))) {
			currentRelationships.push(link);
		}
	} else {
		//no direct relationship found between two nodes
		message = StringResources.get('startingPoints_no_direct_relationship_between_two_nodes', {
			columndIDAndLabelFrom: from,
			columndIDAndLabelTo: to
		});
	}
	const showMessage = options.showMessage;
	const targetModel = options.targetModel;
	if(targetModel && showMessage && message && message.length) {
		targetModel.setActionInfoStatus(message);
	}
	return result;
}

/**
 * Get the scoped raw data of current scope
 * @param {object} rawData
 * @param {object} targetModel
 */
function getScopedRawData(rawData, targetModel) {
	const scope = targetModel.scope;
	const scopedFields = scope.getScopedFields();
	let scopedRawData = null;
	if(scopedFields) {
		scopedRawData = scopeFields(rawData, { scopedFields: scopedFields.map(scopedField => scopedField.fieldIDForExpression), targetId: scope.fieldId });
		//Need to sort
		if(scopedRawData) {
			const relationshipResults = scopedRawData.relationshipStrength && scopedRawData.relationshipStrength.results;
			if(relationshipResults && relationshipResults.length) {
				relationshipResults[0].relationships.sort((relationship1, relationship2) => relationship2.strength - relationship1.strength);
			}
		}
	}
	return scopedRawData;
}

/**
* This function filters the raw data from Predict response to select only nodes and relationships that satisfy user settings.
* @param {Object} rawData - Predict response from relationshipStrength request, including a set of nodes and links
* @param {Object} filterCondition - filter condition (
* targetId: string, // target in the raw data
* showPrimaryRelationshipView: boolean, // determine what type of relationship to display
* maxNodesToDisplay: number,
* maxLinksToDisplay: number,
* filterStrength: {min: int, max: int} or null (if first time loading)
* )
*/
function filterRelationshipStrength(rawData, filterCondition) {
	const linkDataset = rawData && rawData.relationshipStrength && rawData.relationshipStrength.results && rawData.relationshipStrength.results.length ? rawData.relationshipStrength.results[0].relationships:[];

	const triagedLinks = triageLinks(linkDataset, filterCondition.targetId);

	// Prioritize primary links for both views
	let result = filterPrimaryLinks(triagedLinks.primaryLinks, filterCondition);
	if (result.links.length === 0) {
		// no primary links satisfy strength range, stop here
		return result;
	}
	// Continue to add link for secondary relationship view
	if (!filterCondition.showPrimaryRelationshipView && triagedLinks.secondaryLinks.length > 0) {
		const context = result;
		// memorize primary links filterStrengthInfo
		const { filterStrengthInfo: primaryLinksfilterStrengthInfo } = result;
		const primaryLinksCount = result.links.length;
		result = filterSecondaryLinks(triagedLinks.secondaryLinks, filterCondition, context);
		if (result.links.length > primaryLinksCount) {
			// adjust min, max strength after adding secondary links
			result.filterStrengthInfo = adjustMinMaxStrength(result.links, primaryLinksCount);
		} else {
			// if no secondary links added, restore primary links filterStrengthInfo
			result.filterStrengthInfo = primaryLinksfilterStrengthInfo;
		}
	}
	return result;
}

/**
* This function returns the primary links that satisfy filter condition.
* @param {Object[]} primaryLinks - array of link objects
* @param {Object} - filter condition (targetId: string, maxNodesToDisplay: int, filterStrength {min: int, max: int})
*/
function filterPrimaryLinks(primaryLinks, { targetId, maxNodesToDisplay, filterStrength }) {
	// If no links satisfy filter strength condition, return single node
	const nodes = [targetId];
	const links = [];
	let done = false, i = 0;
	if (primaryLinks && primaryLinks.length) {
		while (!done && (nodes.length < maxNodesToDisplay)) {
			const link = primaryLinks[i++];
			if (linkStrengthInRange(link, filterStrength)) {
				nodes.push(link.fieldA === targetId ? link.fieldB : link.fieldA);
				links.push(link);
			}
			if (i === primaryLinks.length || (filterStrength && filterStrength.min > link.strength) ) {
				done = true;
			}
		}
		// Handle the scenario where there are many nodes having the same min or max strength: in this case, even we hit 10 nodes, we still add these nodes in the diagram until we hit 100 nodes
		const maxTotalNodes = Config.max_nodes_to_display;
		if (nodes.length < maxTotalNodes && (i === maxNodesToDisplay - 1) && i < primaryLinks.length) {
			addLinksWithTightStrengths(nodes, links, i , targetId, primaryLinks);
		}
	}
	// min/max strength is set to default values if no primary links found
	return {
		links: links,
		distinctNodes: nodes,
		// links are sorted in decreasing order, thus max is the first and min should be the last
		filterStrengthInfo: {
			min: links.length ? links[links.length - 1].strength : 10,
			max: links.length ? links[0].strength : 100
		}
	};
}

/**
* This function collects links (and their associated nodes) that have the same strength as the min or max strength collected so far.
* @param {String[]} nodes - array of columnId that have been collected before
* @param {Object[]} links - array of links that have been collected before
* @param {integer} startIndex - position in the source links where we should start to add tight links
* @param {String} targetId - targetId
* @param {Object[]} sourceLinks - array of sorted links in the raw cache, source to get more candidate links
*/
function addLinksWithTightStrengths(nodes, links, startIndex, targetId, sourceLinks) {
	let threshold = null;
	if (links[0].strength === links[links.length - 1].strength) {
		// all collected links have the same strength, this strength is the threshold that allow to add more links
		threshold = links[0].strength;
	} else {
		// min strength !== max strength, the min strength is the threshold
		threshold = links[links.length - 1].strength;
	}
	const maxTotalNodes = Config.max_nodes_to_display;
	while (nodes.length < maxTotalNodes && startIndex < sourceLinks.length && sourceLinks[startIndex].strength === threshold) {
		const link = sourceLinks[startIndex];
		nodes.push(link.fieldA === targetId ? link.fieldB : link.fieldA);
		links.push(link);
		startIndex++;
	}
}

/**
* This function returns the secondary links that satisfy filter condition.
* A secondary link can only be added if it's connected to the set of nodes previously collected to avoid isolate islands.
* As the diagram grows after each iteration, make this function recursive to make sure we don't miss any potential secondary links until one of the filter condition breaks.
* @param {Object[]} secondaryLinks - array of potential secondary links to add in the diagram
* @param {Object} - filter condition (maxNodesToDisplay: int, maxLinksToDisplay: int, filterStrength {min: int, max: int})
* @param {Object} - context : set of links and nodes that are already collected and connected with each other, used to verify if these
* nodes can be connected with a new candidate secondary link
*/
function filterSecondaryLinks(secondaryLinks, filterCondition, context) {
	const { maxNodesToDisplay, maxLinksToDisplay, filterStrength } = filterCondition;
	const { links, distinctNodes } = context;
	let done = false, i = 0;
	const remainingLinks = [];
	// For each potential input link, if it's connected to any nodes previously collected (context.distinctNodes) and satisfy filter condition, collect it.
	// If it satisfies filter condition but not yet connected to context, store it in remainingLinks for the next round in recursive process.
	while (!done && (distinctNodes.length < maxNodesToDisplay)) {
		const link = secondaryLinks[i++];
		if (linkStrengthInRange(link, filterStrength) && links.length < maxLinksToDisplay) {
			const seenNodes = _.intersection(distinctNodes, [link.fieldA, link.fieldB]);
			// only add link if it is connected (at least one of the end nodes should be previously added)
			if (seenNodes.length > 0) {
				links.push(link);
				if (seenNodes.length === 1) {
					// Only 1 node of the link is collected, need to add the other node
					if (seenNodes[0] === link.fieldA) {
						distinctNodes.push(link.fieldB);
					} else {
						distinctNodes.push(link.fieldA);
					}
				}
			} else {
				// link satisfy filter condition but not connected, store it for next round of recursive process
				remainingLinks.push(link);
			}
		}
		if (i === secondaryLinks.length || (filterStrength && filterStrength.min > link.strength) || (links.length === maxLinksToDisplay) ) {
			done = true;
		}
	}
	// If we still have room to add nodes or links and still have potential links to add,
	// recursively process these links until no remaining links or filter condition breaks.
	if ((distinctNodes.length < maxNodesToDisplay) && (remainingLinks.length > 0) && (remainingLinks.length < secondaryLinks.length) && links.length < maxLinksToDisplay) {
		filterSecondaryLinks(remainingLinks, filterCondition, context);
	} else if ((distinctNodes.length >= maxNodesToDisplay) && (secondaryLinks.length > 0)) {
		// max nodes is reached, can only add secondary links that have both ends already in until maxLinks is reached
		const slinks = remainingLinks.length > 0 ? remainingLinks : secondaryLinks;
		slinks.forEach((link) => {
			if (linkStrengthInRange(link, filterStrength) && links.length < maxLinksToDisplay) {
				const seenNodes = _.intersection(distinctNodes, [link.fieldA, link.fieldB]);
				if (seenNodes.length === 2) {
					links.push(link);
				}
			}
		});
	}
	return {
		'links': links,
		'distinctNodes': distinctNodes
	};
}
/**
* Links (from Predict response) are always sorted by decreasing order.
* We need to keep the decreasing order in primary and secondary links after triage to make sure
* we always select strongest links for the limit number of nodes.
* We also rely on this order to calculate/adjust the min and max strength after filter links.
*/
function triageLinks(linkDataset, targetId) {
	const primaryLinks = [];
	const secondaryLinks = [];
	// First pass: triage primary and secondary relationships
	linkDataset.forEach((link) => {
		if (link.fieldA === targetId || link.fieldB === targetId) {
			primaryLinks.push(link);
		} else {
			secondaryLinks.push(link);
		}
	});
	return {
		primaryLinks: primaryLinks,
		secondaryLinks: secondaryLinks
	};
}

function linkStrengthInRange(link, filterLinkStrength) {
	if (filterLinkStrength) {
		if (link.strength < filterLinkStrength.min || link.strength > filterLinkStrength.max) {
			return false;
		}
	}
	return true;
}

/**
* The primary links are added in the decreasing order of strength, so no need to sort.
* The secondary links are not guaranteed to be added in this order, so sort them before comparing with primary links.
*/
function adjustMinMaxStrength(links, primaryLinksCount) {
	const minPrimaryLink = links[primaryLinksCount -1].strength;
	const maxPrimaryLink = links[0].strength;
	// sort secondary links strength
	const sLinksStrength = links.slice(primaryLinksCount).map(link => link.strength);
	// sort ascending
	sLinksStrength.sort((a, b) => a - b);
	const last = sLinksStrength.length - 1;
	return {
		min: sLinksStrength[0] < minPrimaryLink ? sLinksStrength[0] : minPrimaryLink,
		max: sLinksStrength[last] > maxPrimaryLink ? sLinksStrength[last] : maxPrimaryLink
	};
}
/**
* This function build the diagram data as per structure of Predict response to make it consumable by dataProvider
* @param {object} rawData - Predict response data
* @param {object} filterResult - diagram data after applying transformation, contains only array of nodes and links
@ @return {object} filterResult in formatted structure
*/
function formatFilterResult(rawData, filterResult) {
	const isFilteredNode = (id) => filterResult.distinctNodes.indexOf(id) > -1;
	return {
		...rawData,
		nodesDataset: {
			...rawData.nodesDataset,
			groupings: rawData.nodesDataset.groupings.map(
				grouping => ({
					...grouping,
					groups: grouping.groups.map(
						group => ({
							...group,
							//Only keep members if they haven't been deleted
							members: group.members.filter(
								({ id }) => isFilteredNode(id)
							)
						})
					).filter(
						//Only keep groups if they have members which have not been filtered out
						({ members }) => !!members.length
					)
				})
			)
		},
		relationshipStrength: {
			...rawData.relationshipStrength,
			results: [{
				relationships: filterResult.links
			}]
		}
	};
}

function addFields(diagramData, rawData, { addedFields, targetId, showPrimaryRelationshipView }){
	let result = null;
	if(addedFields && addedFields.length > 0){
		if (showPrimaryRelationshipView) {
			result = addFieldsForPrimaryRelationshipView(addedFields, diagramData, rawData, targetId);
		} else {
			result = addFieldsForSecondaryRelationshipView(addedFields, diagramData, rawData);
		}
	}
	return result;
}

function addFieldsForPrimaryRelationshipView(addedFields, diagramData, rawData, targetId) {
	const result = {
		foundNodes: [],
		unfoundNodeIds: []
	};
	addedFields.forEach( (addedField)=> {
		const resultSingleField = addSingleField(diagramData, rawData, { fieldId: addedField, targetId: targetId });
		if (resultSingleField && resultSingleField.node) {
			result.foundNodes.push(resultSingleField.node);
		} else {
			result.unfoundNodeIds.push(addedField);
		}
	});
	return result;
}

function addFieldsForSecondaryRelationshipView(addedFields, diagramData, rawData) {
	const result = {
		foundNodes: [],
		unfoundNodeIds: [],
		nodesToAdd: [],
		linksToAdd: []
	};
	const searchFromDiagram = findNodesFromCache(addedFields, diagramData);
	if (searchFromDiagram.foundNodes.length) {
		result.foundNodes = result.foundNodes.concat(searchFromDiagram.foundNodes);
	}
	if (searchFromDiagram.unfoundNodeIds.length) {
		const searchFromRawCache = findNodesFromCache(searchFromDiagram.unfoundNodeIds, rawData);
		if (searchFromRawCache.foundNodes.length) {
			searchFromRawCache.foundNodes.forEach( (foundNode)=> {
				const fieldId = foundNode.members[0].id;
				const linksToAdd = findLinkWithAnyNodeInDiagram(diagramData, rawData, fieldId);
				if (linksToAdd.length) {
					result.nodesToAdd.push(foundNode);
					result.linksToAdd  = result.linksToAdd.concat(linksToAdd);
				} else {
					result.unfoundNodeIds.push(fieldId);
				}
			});
		}
		if (searchFromRawCache.unfoundNodeIds.length) {
			result.unfoundNodeIds = result.unfoundNodeIds.concat(searchFromRawCache.unfoundNodeIds);
		}
	}
	if (result.nodesToAdd.length) {
		// Add group nodes in the diagramData
		diagramData.nodesDataset.groupings[0].groups = diagramData.nodesDataset.groupings[0].groups.concat(result.nodesToAdd);
		result.foundNodes = result.foundNodes.concat(result.nodesToAdd);
	}
	if (result.linksToAdd.length) {
		// Add new links in the diagramData
		diagramData.relationshipStrength.results[0].relationships = diagramData.relationshipStrength.results[0].relationships.concat(result.linksToAdd);
	}
	return result;
}

/**
 * Add a single field to existing diagram
 * @param {Object} diagramData
 * @param {Object} rawData
 * @param {String} fieldId
 * @param {String} targetId
 * @param {boolean} showPrimaryRelationshipView True, show primary relaitonship only
 * @return {Object} that has nodes, links and informaiton about if direct links are found or not
 */
function addSingleField(diagramData, rawData, { fieldId, targetId }){
	const result = findNodeAndLinks(diagramData, rawData, { fieldId: fieldId, targetId: targetId, showPrimaryRelationshipView: true });
	if(result && result.node && result.links && result.links.length) {
		// Add node and links in diagram if they are not already in
		const currentgroups = diagramData.nodesDataset.groupings[0].groups;
		if(!currentgroups.find((group) => group.groupId === result.node.groupId)) {
			currentgroups.push(result.node);

			const currentRelationships = diagramData.relationshipStrength.results[0].relationships;
			result.links.forEach( (entry)=>{
				if(currentRelationships.indexOf(entry) === -1) {
					currentRelationships.push(entry);
				}
			});
		}
	}
	return result;
}

function scopeFields(rawData, { scopedFields, targetId }) {
	const diagramData = DataBuilder.createSingleNode(targetId, 'completed');
	scopedFields.forEach( (scopedField)=>{
		const result = findNodeAndLinks({}, rawData, { fieldId: scopedField, targetId }, [...scopedFields, targetId] );
		if(result && result.node && result.links && result.links.length){
			diagramData.nodesDataset.groupings[0].groups.push(result.node);
			const currentRelatioships = diagramData.relationshipStrength.results[0].relationships;
			result.links.forEach( (entry)=>{
				if(currentRelatioships.indexOf(entry) === -1) {
					currentRelatioships.push(entry);
				}
			});
		}
	});
	return diagramData;
}

/**
 * Find node and links in given rawData for given field
 * @param  {object} diagramData    diagram data that holds node dataste and link datasets
 * @param {object} rawData 			Predict response for request of 100 nodes
 * @param  {String} fieldId		  field id to search
 * @param  {String} targetId		  Id of the target
 * @param {boolean} appendToBase if True, merge the new node and links to base raw data
 * @param  {boolean} showPrimaryRelationshipView the current view type of the target
 * @param  {array} scope if given, then all nodes connected to the found node should be in the scope
*/
function findNodeAndLinks( diagramData, rawData, { fieldId, targetId, showPrimaryRelationshipView }, scope){
	if(fieldId && targetId && diagramData){
		const localAllNodesCache = rawData;
		const node = findSingleNodeFromCache(fieldId, rawData);
		if(node){
			const resultsCache = localAllNodesCache.relationshipStrength && localAllNodesCache.relationshipStrength.results;
			if(resultsCache && resultsCache.length >0){
				const addedRelationships = resultsCache[0].relationships.filter( (entry)=> {
					if (entry.fieldB === fieldId && entry.fieldA === targetId || entry.fieldB === targetId && entry.fieldA === fieldId) {
						return true;
					}
					if (!showPrimaryRelationshipView) {
						if (scope && scope.length) {
							return entry.fieldB === fieldId && scope.indexOf(entry.fieldA) !== -1 || entry.fieldA === fieldId && scope.indexOf(entry.fieldB) !== -1;
						} else {
							// Find if there are links between this field and any nodes in the diagram
							return findLinkWithAnyNodeInDiagram(diagramData, rawData, fieldId);
						}
					}
				});
				if (addedRelationships.length) {
					return {
						node: node,
						links: addedRelationships
					};
				}
			}
		}
	}
	return null;
}

function findLinkWithAnyNodeInDiagram(diagramData, rawData, fieldId) {
	const rawLinks = rawData.relationshipStrength.results[0].relationships;
	const links = [];
	if (rawLinks) {
		// Get all links and nodes from raw cache that link to the field
		const foundLinksInRaw = rawLinks.filter(link => link.fieldA === fieldId || link.fieldB === fieldId);
		if (foundLinksInRaw.length) {
			const otherNodes = foundLinksInRaw.map(link => {
				if (link.fieldA === fieldId) {
					return link.fieldB;
				} else {
					return link.fieldA;
				}
			});
			const diagramNodes = getAllFieldNodesFromDiagramCache(diagramData, false /*exclude target*/);
			// intersection between diagram nodes and nodes that have link with the field
			const commonNodes = _.intersection(otherNodes, diagramNodes);
			if (commonNodes.length > 0) {
				commonNodes.forEach(commonNode => {
					const secondaryLink = foundLinksInRaw.find(link => link.fieldA === commonNode || link.fieldB === commonNode);
					if (secondaryLink) {
						links.push(secondaryLink);
					}
				});
			}
		}
	}
	return links;
}

function findNodesFromCache(nodeIds, rawData){
	const result = {
		foundNodes: [],
		unfoundNodeIds:[]
	};
	if(nodeIds && nodeIds.length){
		nodeIds.find((nodeId) => {
			const node = findSingleNodeFromCache(nodeId, rawData);
			if(node){
				result.foundNodes.push(node);
			}else{
				result.unfoundNodeIds.push(nodeId);
			}
		}
		);
	}
	return result;
}

/**
 * Find all nodes from data cache, excluding focus field if needed
 *
 * @param {Object} response cache that contains node dataset and link dataset
 * @param {boolean} excludeFocusField if True, exclude focus field, False, not.
 */
function getAllFieldNodesFromDiagramCache(cache, excludeFocusField = true){
	const result = [];
	const groupingsCache = cache && cache.nodesDataset && cache.nodesDataset.groupings;
	if(groupingsCache && groupingsCache.length >0){
		const groupsCache = groupingsCache[0].groups;
		if(groupsCache && groupsCache.length >0){
			groupsCache.forEach((entry)=> {
				const member = entry.members[0];
				if(! (excludeFocusField && member.type === 'focusField')){
					result.push(member.id);
				}
			});
		}
	}
	return result;
}


/**
 * get single node from cached reponse data
 * @param {string} nodeID
 * @param {object} cachedData
 */
function findSingleNodeFromCache(nodeID, cachedData){
	let node = null;
	if(nodeID && cachedData && cachedData.nodesDataset && cachedData.nodesDataset.groupings){
		const groupingsCache = cachedData.nodesDataset.groupings;
		if(groupingsCache && groupingsCache.length >0){
			const groupsCache = groupingsCache[0].groups;
			if(groupsCache && groupsCache.length >0){
				node = groupsCache.find( (entry) => {
					if(entry && entry.members && entry.members.length >0){
						return entry.members[0].id === nodeID;
					}
				});
			}
		}
	}
	return node;
}

/**
 * Check if there is direct link between two nodes
 * @param  {[type]} fieldOne  first node of the link
 * @param  {[type]} fieldTwo  second node of the link
 * @param  {[type]} cachedData Cache that holds the node dataset and link dataset
 * @return {object} {nodeOne, nodeTwo, link}- nodeOne is the node for`fieldOne`, nodeTwo is the node for`fieldTwo`
 */
function findDirectLink(fieldOne, fieldTwo, cachedData){
	const nodeOne = findSingleNodeFromCache(fieldOne, cachedData);
	const nodeTwo = findSingleNodeFromCache(fieldTwo, cachedData);
	if(nodeOne && nodeTwo) {
		if(cachedData && cachedData.relationshipStrength){
			const results = cachedData.relationshipStrength.results;
			if(results && results.length){
				const matchedResult = results[0].relationships.find( (link) =>{
					return link.fieldA === fieldOne && link.fieldB === fieldTwo || link.fieldA === fieldTwo && link.fieldB === fieldOne;
				});
				if(matchedResult) {
					return {
						nodeOne,
						nodeTwo,
						link: matchedResult
					};
				}
			}
		}
	}
	return null;
}


function formatLinkStrength(link) {
	if(link) {
		if (!Number.isInteger(link.strength)) {
			link.strength = Math.round(link.strength * 100);
		} else if (link.strength === 1) {
			link.strength = 100;
		}
	}
	return link;
}

export {
	getGroupLabel,
	getFieldLabel,
	getGroupTypeAndGroupLabel,
	getDataVerificationResults,
	printMsg,
	applyTransforms,
	getConnectedFields,
	getOrphanFields,
	removeDeletedFields,
	filterRelationshipStrength,
	filterPrimaryLinks,
	filterSecondaryLinks,
	triageLinks,
	adjustMinMaxStrength,
	formatFilterResult,
	linkStrengthInRange,
	addFields,
	addSingleField,
	findSingleNodeFromCache,
	findNodesFromCache,
	getAllFieldNodesFromDiagramCache,
	findDirectLink,
	formatLinkStrength,
	findLinkWithAnyNodeInDiagram
};



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipUtil.js